import OverflowTooltip from '@components/OverflowTooltip/OverflowTooltip';
import { Input } from '@hyperflake/react-ui-library';
import { CrossIcon, SearchIcon } from '@icon/index';
import { useFloatingElement } from '@library/floating';
import { List, ListItem } from '@library/list';
import {
    ChangeEvent,
    DetailedHTMLProps,
    FocusEvent,
    InputHTMLAttributes,
    KeyboardEvent,
    useCallback,
    useState,
} from 'react';

interface SingleCategoryInputProps extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    categories: string[];
    selectedCategory: string;
    onSelectedCategoryChange: (category: string) => void;
}

const SingleCategoryInput = (props: SingleCategoryInputProps) => {
    const { categories, selectedCategory, onSelectedCategoryChange, disabled, ...rest } = props;

    const [inputValue, setInputValue] = useState('');
    const [isSearchMode, setIsSearchMode] = useState(false);

    const { context, refs, getReferenceProps, getFloatingProps } = useFloatingElement<HTMLInputElement>({
        placement: 'bottom-start',
        offset: 8,
        triggers: [],
    });

    const handleFocus = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            setIsSearchMode(true);
            setInputValue('');
            context.onOpenChange(true);
        },
        [context]
    );

    const handleBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            // Small delay to allow for option selection
            setTimeout(() => {
                const val = e.target.value.trim();

                // If user typed something and it's not empty, add it as new category
                if (val && !categories.includes(val)) {
                    onSelectedCategoryChange(val);
                }

                setIsSearchMode(false);
                setInputValue('');
                context.onOpenChange(false);
            }, 150);
        },
        [context, categories, onSelectedCategoryChange]
    );

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setInputValue(e.target.value);
            context.onOpenChange(true);
        },
        [context]
    );

    const handleKeyUp = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            const val = (e.target as HTMLInputElement).value.trim();

            if (e.key === 'Enter' && val !== '') {
                onSelectedCategoryChange(val);
                setIsSearchMode(false);
                setInputValue('');
                context.onOpenChange(false);
            }
        },
        [context, onSelectedCategoryChange]
    );

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    }, []);

    const handleSelect = useCallback(
        (category: string) => {
            onSelectedCategoryChange(category);
            setIsSearchMode(false);
            setInputValue('');
            context.onOpenChange(false);
            refs.domReference.current?.blur();
        },
        [context, onSelectedCategoryChange, refs.domReference]
    );

    const handleClear = useCallback(() => {
        onSelectedCategoryChange('');
        setInputValue('');
        refs.domReference.current?.focus();
    }, [onSelectedCategoryChange, refs.domReference]);

    // Filter categories based on input
    const filteredCategories = useCallback(() => {
        if (!inputValue.trim()) return categories;

        const searchTerm = inputValue.toLowerCase();
        return categories.filter((category) => category.toLowerCase().includes(searchTerm));
    }, [categories, inputValue]);

    const displayValue = isSearchMode ? inputValue : selectedCategory;
    const placeholder = selectedCategory ? selectedCategory : 'Search or add category...';

    return (
        <div className="relative">
            <div className="relative flex items-center">
                <div className="absolute left-4 z-10">
                    <SearchIcon className="text-grayscale-400" />
                </div>

                <Input
                    {...rest}
                    {...getReferenceProps()}
                    ref={refs.setReference}
                    value={displayValue}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    disabled={disabled}
                    className="pl-9 pr-10"
                    placeholder={isSearchMode ? 'Search or add category...' : placeholder}
                    autoComplete="off"
                />

                {selectedCategory && !isSearchMode && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-3 p-1 hover:bg-gray-100 rounded"
                        disabled={disabled}
                    >
                        <CrossIcon className="w-2 h-2 text-grayscale-400" />
                    </button>
                )}
            </div>

            {/* Selected category display */}
            {selectedCategory && !isSearchMode && (
                <div className="mt-2 px-3 py-1 bg-primary/10 text-primary rounded-md text-sm inline-block">
                    {selectedCategory}
                </div>
            )}

            {/* Dropdown list */}
            {context.open && (
                <List
                    {...getFloatingProps()}
                    style={{ ...context.floatingStyles }}
                    ref={refs.setFloating}
                    className="w-full z-10 max-h-60 overflow-y-auto"
                >
                    {filteredCategories().map((category, i) => (
                        <ListItem
                            key={i}
                            onClick={() => handleSelect(category)}
                            onMouseDown={(e) => e.preventDefault()}
                            hover
                            className={selectedCategory === category ? 'bg-primary/10' : ''}
                        >
                            <OverflowTooltip>{category}</OverflowTooltip>
                        </ListItem>
                    ))}

                    {/* Add new category option */}
                    {inputValue.trim() && !categories.includes(inputValue.trim()) && (
                        <ListItem
                            onClick={() => handleSelect(inputValue.trim())}
                            onMouseDown={(e) => e.preventDefault()}
                            hover
                            className="border-t border-gray-200 text-primary"
                        >
                            <div className="flex items-center gap-2">
                                <span>+</span>
                                <span>Add "{inputValue.trim()}"</span>
                            </div>
                        </ListItem>
                    )}
                </List>
            )}
        </div>
    );
};

export default SingleCategoryInput;
